import React, { useState, useEffect } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, FormControlLabel, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import './mymaps.css';

const BusCheckboxes = ({ selectedElements, handleSelectElement, handleRemoveElement }) => {
  const [busRoutes, setBusRoutes] = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState({});
  const [initialLoad, setInitialLoad] = useState(true);
  const [selectedZones, setSelectedZones] = useState([]);

  // Definición de las zonas y sus líneas
  const zones = {
    'Norte': ['21', '29', '44', '60', '76', '78', '87', '93', '108', '117', '118', '130', '204', '228', '341', '391', '720', '722', '723'],
    'Sur': ['26','31', '33', '45', '46', '70', '85','98', '100', '115', '119', '150', '164', '179', '188', '271', '295', '299', '324', '384', '421', '501', '524', '527'],
    'Oeste': ['1', '8', '23', '25', '46', '91', '99', '115','132', '135', '150', '244', '302', '303', '390', '461', '462', '463', '464'],
    'Todos': []
  };

  // Cargar todas las rutas base disponibles
  useEffect(() => {
    const fetchAllBaseRoutes = async () => {
      try {
        setInitialLoad(true);
        
        const response = await axios.get('https://mapa.ne3publicidad.com.ar/api/api/getLinea/.*');
        
        const baseRoutesMap = new Map();
        
        response.data.forEach(item => {
          const baseRoute = item.linea.replace(/[^0-9]/g, '');
          if (baseRoute) {
            if (!baseRoutesMap.has(baseRoute)) {
              baseRoutesMap.set(baseRoute, {
                base: baseRoute,
                color: item.color || '#FF0000'
              });
            }
          }
        });
  
        // Convertir a array y ordenar numéricamente
        const uniqueBaseRoutes = Array.from(baseRoutesMap.values())
          .sort((a, b) => {
            // Convertir a números para comparar
            const numA = parseInt(a.base, 10);
            const numB = parseInt(b.base, 10);
            return numA - numB;
          });
  
        setBusRoutes(uniqueBaseRoutes);
        
      } catch (error) {
        console.error("Error fetching base routes:", error);
        
        try {
          const alternativeResponse = await axios.get('https://mapa.ne3publicidad.com.ar/api/api/getLineasDistinct');
          const routes = alternativeResponse.data.map(item => ({
            base: item.linea.replace(/[^0-9]/g, ''),
            color: item.color || '#FF0000'
          })).filter(route => route.base);
          
          // Eliminar duplicados y ordenar
          const uniqueRoutes = routes
            .filter((route, index, self) => 
              index === self.findIndex(r => r.base === route.base)
            )
            .sort((a, b) => {
              const numA = parseInt(a.base, 10);
              const numB = parseInt(b.base, 10);
              return numA - numB;
            });
          
          setBusRoutes(uniqueRoutes);
        } catch (altError) {
          console.error("Alternative method failed:", altError);
        }
      } finally {
        setInitialLoad(false);
      }
    };
  
    fetchAllBaseRoutes();
  }, []);

  // Efecto para manejar la selección de zonas
  useEffect(() => {
    if (initialLoad) return;

    const handleZoneSelection = async () => {
      // Si se seleccionó "Todos", manejar de manera especial
      if (selectedZones.includes('Todos')) {
        // Seleccionar todas las rutas
        const allRoutes = busRoutes.map(route => route.base);
        const routesToAdd = allRoutes.filter(route => !selectedRoutes.includes(route));
        
        for (const route of routesToAdd) {
          await fetchRouteData(route);
        }
        
        // Desmarcar otras zonas
        setSelectedZones(['Todos']);
        return;
      }

      // Obtener todas las rutas de las zonas seleccionadas
      const zoneRoutes = selectedZones.flatMap(zone => zones[zone]);
      
      // Filtrar rutas que ya están seleccionadas
      const routesToAdd = zoneRoutes.filter(route => !selectedRoutes.includes(route));
      
      // Filtrar rutas que están seleccionadas pero no están en las zonas seleccionadas
      const routesToRemove = selectedRoutes.filter(route => 
        !zoneRoutes.includes(route) && 
        !selectedZones.some(zone => zones[zone].includes(route))
      );
      
      // Eliminar rutas que no pertenecen a las zonas seleccionadas
      routesToRemove.forEach(route => {
        handleRemoveElement(route, 'bus');
        setSelectedRoutes(prev => prev.filter(r => r !== route));
      });
      
      // Agregar nuevas rutas de las zonas seleccionadas
      for (const route of routesToAdd) {
        await fetchRouteData(route);
      }
    };

    handleZoneSelection();
  }, [selectedZones, busRoutes]);

  const fetchRouteData = async (baseRoute) => {
    try {
      setLoadingRoutes(prev => ({ ...prev, [baseRoute]: true }));
      
      const response = await axios.get(`https://mapa.ne3publicidad.com.ar/api/api/getLinea/${baseRoute}`);
      
      const variantsMap = new Map();
      
      response.data.forEach(point => {
        const variant = point.linea.replace(baseRoute, '') || 'A';
        if (!variantsMap.has(variant)) {
          variantsMap.set(variant, {
            points: [],
            color: point.color || '#FF0000'
          });
        }
        variantsMap.get(variant).points.push({
          lat: parseFloat(point.lat),
          lng: parseFloat(point.lng),
          sequence: point.sequence_order || 0
        });
      });
  
      variantsMap.forEach((variantData, variant) => {
        const fullRoute = `${baseRoute}${variant === 'A' ? '' : variant}`;
        const points = variantData.points.sort((a, b) => a.sequence - b.sequence);
        
        const cleanedPath = [];
        const pointMap = new Map();
  
        points.forEach(point => {
          const key = `${point.lat},${point.lng}`;
          if (!pointMap.has(key)) {
            pointMap.set(key, true);
            cleanedPath.push(point);
          }
        });
  
        if (cleanedPath.length > 2) {
          const first = cleanedPath[0];
          const last = cleanedPath[cleanedPath.length - 1];
          const distance = Math.sqrt(
            Math.pow(last.lat - first.lat, 2) + 
            Math.pow(last.lng - first.lng, 2)
          );
          
          if (distance < 0.0001) {
            cleanedPath.pop();
          }
        }
  
        if (cleanedPath.length > 0) {
          handleSelectElement(fullRoute, cleanedPath, 'bus', variantData.color);
        }
      });
      
      // Ordenar las rutas seleccionadas al añadir una nueva
      setSelectedRoutes(prev => [...prev, baseRoute]
        .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
      );
    } catch (error) {
      console.error(`Error loading route ${baseRoute}:`, error);
    } finally {
      setLoadingRoutes(prev => ({ ...prev, [baseRoute]: false }));
    }
  };

  const handleRouteToggle = (baseRoute) => {
    // Si estamos en modo de zona, no permitir cambios individuales
    if (selectedZones.length > 0 && !selectedZones.includes('Todos')) return;

    if (selectedRoutes.includes(baseRoute)) {
      // Eliminar todas las variantes de esta ruta
      handleRemoveElement(baseRoute, 'bus');
      setSelectedRoutes(prev => prev.filter(r => r !== baseRoute));
    } else {
      fetchRouteData(baseRoute);
    }
  };

  const handleZoneToggle = (zone) => {
    setSelectedZones(prev => {
      if (zone === 'Todos') {
        // Si se selecciona "Todos", deseleccionar otras zonas
        return prev.includes('Todos') ? [] : ['Todos'];
      } else {
        // Si se selecciona otra zona, deseleccionar "Todos" si está seleccionado
        const newZones = prev.includes('Todos') 
          ? [] 
          : [...prev];
        
        if (newZones.includes(zone)) {
          return newZones.filter(z => z !== zone);
        } else {
          return [...newZones, zone];
        }
      }
    });
  };

  if (initialLoad) {
    return (
      <div className="accordion-container">
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} className="accordion-button">
            <h3>Recorrido de Buses</h3>
          </AccordionSummary>
          <AccordionDetails className="accordion-details">
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    );
  }

  return (
    <div className="accordion-container">
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className="accordion-button">
          <h3>Recorrido de Buses</h3>
        </AccordionSummary>
        <AccordionDetails className="accordion-details">
          <div className="checkbox-container">
            {busRoutes.map(route => (
              <div key={route.base} className="route-group">
                <FormControlLabel
                  control={
                    <div
                      className={`custom-checkbox ${selectedRoutes.includes(route.base) ? 'selected' : ''}`}
                      onClick={() => handleRouteToggle(route.base)}
                      style={{
                        borderColor: route.color,
                        opacity: loadingRoutes[route.base] ? 0.7 : 1,
                        cursor: loadingRoutes[route.base] 
                          ? 'wait' 
                          : (selectedZones.length > 0 && !selectedZones.includes('Todos')) 
                            ? 'not-allowed' 
                            : 'pointer',
                        width: '50px',
                        height: '20px'
                      }}
                      disabled={loadingRoutes[route.base] || (selectedZones.length > 0 && !selectedZones.includes('Todos'))}
                    >
                      {route.base}
                      {loadingRoutes[route.base] && (
                        <CircularProgress size={16} style={{ marginLeft: 8, color: route.color }} />
                      )}
                    </div>
                  }
                  style={{
                    marginLeft: 0,
                    marginRight: 0,
                    marginBottom: '1px'
                  }}
                />
              </div>
            ))}
          </div>
          
          {/* Checkboxes de zonas */}
          <div className="zones-container" style={{ 
            marginTop: '20px', 
            paddingTop: '10px', 
            borderTop: '1px solid #ccc',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '10px'
          }}>
            {Object.keys(zones).map(zone => (
              <div key={zone} className="zone-group">
                <FormControlLabel
                  control={
                    <div
                      className={`custom-checkbox zone-checkbox ${selectedZones.includes(zone) ? 'selected' : ''}`}
                      onClick={() => handleZoneToggle(zone)}
                      style={{
                        borderColor: '#176A36',
                        backgroundColor: selectedZones.includes(zone) ? '#FFEC00' : '#176A36',
                        color: selectedZones.includes(zone) ? '#000' : '#FFF',
                        padding: '0 15px',
                        cursor: 'pointer'
                      }}
                    >
                      {zone}
                    </div>
                  }
                  style={{
                    marginLeft: 0,
                    marginRight: 0,
                    marginBottom: '1px'
                  }}
                />
              </div>
            ))}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default BusCheckboxes;